import React, { useState, useEffect } from 'react';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import { AdminCardGridShimmer } from '../common/Shimmer.jsx';
import { IconAlertCircle } from '../common/AdminIcons.jsx';
import AdminProductsRepository from '../../services/AdminProductsRepository.js';
import { isProductVisibleInClientContext } from '../../utils/productVisibility.js';
import { resolveProductLicenseKeys } from '../../utils/licenseKeyUtils.js';
import { buildApiUrl } from '../../utils/apiBase.js';
import { useDestructiveAction } from '../../contexts/DestructiveActionContext.jsx';
import { useScreenProbe } from '../../utils/screenTiming.js';
import './AdminInvitationRewards.css';

const EMPTY_REWARD = {
  name: '',
  description: '',
  pointsCost: 1,
  type: 'license_trial',
  config: { licenseName: '', durationDays: 30 },
  isActive: true,
  displayOrder: 0
};

const TYPE_OPTIONS = [
  { value: 'license_trial', label: 'ライセンス試用付与' },
  { value: 'custom', label: 'カスタム特典' }
];

const AdminInvitationRewards = () => {
  const probe = useScreenProbe('invitation-rewards');
  const { requestDestructiveAction } = useDestructiveAction();
  const [rewards, setRewards] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [products, setProducts] = useState([]);

  useEffect(() => {
    loadRewards();
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const repo = AdminProductsRepository.getInstance();
      const all = await repo.getAllProducts();
      setProducts(all.filter((p) => {
        const keys = resolveProductLicenseKeys(p);
        return isProductVisibleInClientContext(p) && keys.length > 0;
      }));
    } catch {
      /* ignore */
    }
  };

  const loadRewards = async () => {
    probe.start('load');
    setIsLoading(true);
    setError('');
    try {
      const res = await fetch(buildApiUrl('/api/admin/invitation/rewards'), { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || '取得に失敗しました');
      setRewards(data.rewards || []);
      probe.end('load', 'ok');
      probe.unlock();
    } catch (err) {
      probe.end('load', `error: ${err?.message || 'unknown'}`);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingReward({ ...EMPTY_REWARD, config: { ...EMPTY_REWARD.config } });
    setIsModalOpen(true);
  };

  const handleEdit = (reward) => {
    setEditingReward({
      ...reward,
      config: reward.config ? { ...reward.config } : { licenseName: '', durationDays: 30 }
    });
    setIsModalOpen(true);
  };

  const handleSave = async () => {
    if (!editingReward.name.trim()) {
      setError('特典名は必須です');
      return;
    }
    if (!editingReward.pointsCost || editingReward.pointsCost < 1) {
      setError('必要ポイントは1以上で指定してください');
      return;
    }

    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      const isNew = !editingReward.id;
      const url = isNew
        ? buildApiUrl('/api/admin/invitation/rewards')
        : buildApiUrl(`/api/admin/invitation/rewards/${editingReward.id}`);

      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: editingReward.name.trim(),
          description: editingReward.description.trim(),
          pointsCost: Number(editingReward.pointsCost),
          type: editingReward.type,
          config: editingReward.config,
          isActive: editingReward.isActive,
          displayOrder: Number(editingReward.displayOrder) || 0
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || '保存に失敗しました');

      setSuccess(isNew ? '特典を作成しました' : '特典を更新しました');
      setIsModalOpen(false);
      setEditingReward(null);
      await loadRewards();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (reward) => {
    if (!reward) return;
    const result = await requestDestructiveAction({
      title: '招待特典削除',
      message: `特典「${reward.name}」を削除します。この操作は取り消せません。`,
      confirmLabel: '削除する',
    });
    if (!result?.reason) return;

    setIsSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(buildApiUrl(`/api/admin/invitation/rewards/${reward.id}`), {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason: result.reason }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || '削除に失敗しました');

      setSuccess(`特典「${reward.name}」を削除しました`);
      await loadRewards();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingReward(null);
  };

  if (isLoading) {
    return <AdminCardGridShimmer cards={4} />;
  }

  return (
    <div className="admin-inv-rewards">
      <div className="section-header">
        <div>
          <h1>招待特典管理</h1>
          <p>招待ポイントで交換可能な特典を管理します</p>
        </div>
        <button className="btn btn-primary" onClick={handleCreate}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          新規作成
        </button>
      </div>

      {success && (
        <div className="admin-inv-alert admin-inv-alert-success">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          {success}
          <button className="admin-inv-alert-close" onClick={() => setSuccess('')}>&times;</button>
        </div>
      )}
      {error && (
        <div className="admin-inv-alert admin-inv-alert-error">
          <IconAlertCircle size={18} />
          {error}
          <button className="admin-inv-alert-close" onClick={() => setError('')}>&times;</button>
        </div>
      )}

      {rewards.length === 0 ? (
        <div className="admin-inv-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
          </svg>
          <p>特典がまだ登録されていません</p>
          <button className="btn btn-primary" onClick={handleCreate}>最初の特典を作成</button>
        </div>
      ) : (
        <div className="admin-inv-table-wrapper">
          <table className="admin-inv-table">
            <thead>
              <tr>
                <th>順序</th>
                <th>特典名</th>
                <th>種別</th>
                <th>必要pt</th>
                <th>状態</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {rewards.map((r) => (
                <tr key={r.id}>
                  <td className="admin-inv-order">{r.displayOrder}</td>
                  <td>
                    <div className="admin-inv-name">{r.name}</div>
                    {r.description && <div className="admin-inv-desc">{r.description}</div>}
                  </td>
                  <td>
                    <span className={`admin-inv-type-badge admin-inv-type-${r.type}`}>
                      {TYPE_OPTIONS.find(t => t.value === r.type)?.label || r.type}
                    </span>
                  </td>
                  <td className="admin-inv-points">{r.pointsCost}pt</td>
                  <td>
                    <span className={`admin-inv-status ${r.isActive ? 'active' : 'inactive'}`}>
                      {r.isActive ? '有効' : '無効'}
                    </span>
                  </td>
                  <td>
                    <div className="admin-inv-actions">
                      <button className="admin-inv-action-btn" onClick={() => handleEdit(r)} title="編集">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                          <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                          <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                      </button>
                      <button className="admin-inv-action-btn admin-inv-action-delete" onClick={() => handleDelete(r)} title="削除">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 作成/編集モーダル */}
      {isModalOpen && editingReward && (
        <div className="modal-overlay">
          <div className="modal-content modal-lg">
            <div className="modal-header">
              <h2>{editingReward.id ? '特典を編集' : '新規特典を作成'}</h2>
              <button className="modal-close" onClick={handleCloseModal}>&times;</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>特典名 <span className="required">*</span></label>
                <textarea
                  rows={2}
                  value={editingReward.name}
                  onChange={(e) => setEditingReward({ ...editingReward, name: e.target.value })}
                  placeholder="例: Synapse GrowthGuide 1か月利用"
                />
              </div>

              <div className="form-group">
                <label>説明</label>
                <textarea
                  value={editingReward.description}
                  onChange={(e) => setEditingReward({ ...editingReward, description: e.target.value })}
                  placeholder="特典の説明"
                  rows={3}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>必要ポイント <span className="required">*</span></label>
                  <input
                    type="number"
                    min="1"
                    value={editingReward.pointsCost}
                    onChange={(e) => setEditingReward({ ...editingReward, pointsCost: Number(e.target.value) })}
                  />
                </div>
                <div className="form-group">
                  <label>表示順序</label>
                  <input
                    type="number"
                    min="0"
                    value={editingReward.displayOrder}
                    onChange={(e) => setEditingReward({ ...editingReward, displayOrder: Number(e.target.value) })}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>種別</label>
                  <select
                    value={editingReward.type}
                    onChange={(e) => setEditingReward({ ...editingReward, type: e.target.value })}
                  >
                    {TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>状態</label>
                  <select
                    value={editingReward.isActive ? 'true' : 'false'}
                    onChange={(e) => setEditingReward({ ...editingReward, isActive: e.target.value === 'true' })}
                  >
                    <option value="true">有効</option>
                    <option value="false">無効</option>
                  </select>
                </div>
              </div>

              {editingReward.type === 'license_trial' && (
                <div className="admin-inv-config-section">
                  <h3>ライセンス試用設定</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>ライセンスキー</label>
                      <select
                        value={editingReward.config?.licenseName || ''}
                        onChange={(e) => setEditingReward({
                          ...editingReward,
                          config: { ...editingReward.config, licenseName: e.target.value }
                        })}
                      >
                        <option value="">選択してください</option>
                        {products.flatMap((p) => {
                          const keys = resolveProductLicenseKeys(p);
                          return keys.map((key) => (
                            <option key={`${p.id}-${key}`} value={key}>
                              {p.name}（{key}）
                            </option>
                          ));
                        })}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>付与日数</label>
                      <input
                        type="number"
                        min="1"
                        value={editingReward.config?.durationDays || 30}
                        onChange={(e) => setEditingReward({
                          ...editingReward,
                          config: { ...editingReward.config, durationDays: Number(e.target.value) }
                        })}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseModal} disabled={isSaving}>
                キャンセル
              </button>
              <button className="btn btn-primary" onClick={handleSave} disabled={isSaving}>
                {isSaving ? '保存中...' : '保存'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInvitationRewards;
